// pages/LinkedAccounts/OthersLinkedAccounts.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import backendURL from '../../config';
import {
    Box, Card, CardContent, Typography, CircularProgress, Alert as MuiAlert, // Use MuiAlert
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper,
    Button, TextField, Autocomplete, Chip, IconButton, Tooltip,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PropTypes from 'prop-types';
import LinkIcon from '@mui/icons-material/Link'; // Used for 'Connected' status and 'Connect' button

// Use MuiAlert for the Alert component
const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="standard" {...props} />;
});


const getSharedAccessStatusChip = (status) => {
    let color = 'default';
    let label = status;
    switch (status?.toLowerCase()) {
        case 'pending': color = 'warning'; label = 'Pending Invitation'; break;
        case 'connected': case 'active': case 'accepted': color = 'success'; label = 'Access Granted'; break;
        case 'declined': color = 'error'; label = 'Declined'; break;
        case 'revoked': case 'cancelled': case 'disconnected': color = 'default'; label = 'Access Revoked'; break;
        default: label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
    return <Chip label={label} color={color} size="small" />;
};

const MainLinkedAccounts = ({ showMessage }) => {
    const [sharedAccessList, setSharedAccessList] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);

    const [emailSearch, setEmailSearch] = useState('');
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [selectedUserToInvite, setSelectedUserToInvite] = useState(null);
    const [isUserSearching, setIsUserSearching] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteFormError, setInviteFormError] = useState('');

    const [actionConfirmOpen, setActionConfirmOpen] = useState(false);
    const [itemForAction, setItemForAction] = useState(null);
    const [currentActionType, setCurrentActionType] = useState(''); // 'cancel', 'resend', 'disconnect'

    const fetchSharedAccessList = useCallback(async () => {
        setListLoading(true);
        setListError(null);

        const token = localStorage.getItem('my_access_token') ? localStorage.getItem('my_access_token') : localStorage.getItem('access_token');

        try {
            const response = await axios.get(`${backendURL}/linked-accounts/manage-access/incoming`, { headers: { 'Authorization': `Bearer ${token}` } });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            setSharedAccessList(responseData || []);
        } catch (err) {
            console.error("Error fetching shared access list:", err);
            const errMsg = err.response?.data?.message || "Failed to load shared access information.";
            setListError(errMsg);
            if (showMessage) showMessage(errMsg, 'error');
        } finally {
            setListLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        fetchSharedAccessList();
    }, [fetchSharedAccessList]);

    const handleOpenActionDialog = (item, action) => {
        setItemForAction(item);
        setCurrentActionType(action);
        setActionConfirmOpen(true);
    };

    const handleCloseActionDialog = () => {
        setActionConfirmOpen(false);
        setItemForAction(null);
        setCurrentActionType('');
    };

    const handleConfirmAction = async () => {
        if (!itemForAction || !currentActionType) return;
        const itemId = itemForAction.id;
        const token = localStorage.getItem('my_access_token') ? localStorage.getItem('my_access_token') : localStorage.getItem('access_token');
        try {
            let status = currentActionType
            if (status == 'ACCEPT'){
                status = 'approve'
            }else if (status == 'DECLINE'){
                status = 'deny'
            }else if (status == 'ACCESS REVOKE'){
                status = 'revoke'
            }else{
                status = 'pending'
            }
            const response = await axios.post(`${backendURL}/linked-accounts/manage-access/${itemId}/${status}`, { }, { headers: { 'Authorization': `Bearer ${token}` } });
            if (showMessage) showMessage(response.data.message, 'success');
            fetchSharedAccessList();
        } catch (error) {
            const errMsg = error.response?.data?.message || `Failed to set update request.`;
            if (showMessage) showMessage(errMsg, 'error');
        } finally {
            // setListLoading(false);
            handleCloseActionDialog();
        }
    };

    return (
        <Box>
            

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" component="div" gutterBottom sx={{ mb: 2 }}>
                    My Accounts shared with
                    </Typography>
                    {listLoading && sharedAccessList.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, minHeight: 150, alignItems:'center' }}><CircularProgress /></Box>
                    ) : listError && sharedAccessList.length === 0 && !listLoading ? (
                        <Alert severity="error" sx={{ m: 2 }}>{listError}</Alert>
                    ) : sharedAccessList.length === 0 && !listLoading && !listError ? (
                        <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            Your account doesnt shared with anyone.
                        </Typography>
                    ) : sharedAccessList.length > 0 ? (
                        <>
                        {listError && <Alert severity="warning" sx={{ mb: 2 }}>{listError} (Displaying cached or older data)</Alert>}
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="shared access table">
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: '600', backgroundColor: 'action.hover' } }}>
                                        <TableCell>Chatbot/Website Name<br /><small>Email</small></TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Request Initiated at</TableCell>
                                        <TableCell>Last Connected at</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sharedAccessList.map((item) => (
                                        <TableRow hover key={item.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>
                                                <Typography variant="body1" component="div">{item.chatbot_name ? item.chatbot_name : (item.website_name ? item.website_name : 'N/A')}</Typography>
                                                <Typography variant="caption" color="textSecondary">{item.email}</Typography>
                                            </TableCell>
                                            <TableCell>{getSharedAccessStatusChip(item.status)}</TableCell>
                                            <TableCell>{item.request_initiated_at || 'N/A'}</TableCell>
                                            <TableCell>{item.last_connected_at || 'N/A'}</TableCell>
                                            <TableCell align="center">
                                                {item.status === 'PENDING' && (
                                                    <Box sx={{display: 'flex', gap: 1, justifyContent: 'center'}}>
                                                        <Tooltip title="Accept Request" arrow>
                                                            <Button variant="outlined" color="success" size="small" startIcon={<LinkIcon />} onClick={() => handleOpenActionDialog(item, 'ACCEPT')} disabled={inviteLoading}>
                                                                Accept
                                                            </Button>
                                                        </Tooltip>
                                                         <Tooltip title="Decline Request" arrow>
                                                            <Button variant="outlined" color="error" size="small" startIcon={<LinkOffIcon />} onClick={() => handleOpenActionDialog(item, 'DECLINE')} disabled={inviteLoading}>
                                                                Decline
                                                            </Button>
                                                        </Tooltip>
                                                         
                                                    </Box>
                                                )}
                                                {item.status === 'ACCESS_GRANTED' && (
                                                    <Tooltip title="Revoke Access" arrow>
                                                        <Button variant="outlined" color="error" size="small" startIcon={<LinkOffIcon />} onClick={() => handleOpenActionDialog(item, 'ACCESS REVOKE')} disabled={inviteLoading}>
                                                            Revoke
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {(item.status === 'DECLINED' || item.status === 'ACCESS_REVOKED') && (
                                                    <Typography variant="caption" color="textSecondary">No actions</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </>
                    ) : null}
                </CardContent>
            </Card>

            <Dialog open={actionConfirmOpen} onClose={handleCloseActionDialog}>
                <DialogTitle>Confirm Action: {currentActionType?.charAt(0).toUpperCase() + currentActionType?.slice(1)}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to {currentActionType} access for <strong>{itemForAction?.name || itemForAction?.email}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseActionDialog}>Cancel</Button>
                    <Button
                        onClick={handleConfirmAction}
                        color={currentActionType === 'DECLINE' || currentActionType === 'ACCESS REVOKE' ? "error" : "primary"}
                        variant="contained"
                        autoFocus
                    >
                        Confirm {currentActionType?.charAt(0).toUpperCase() + currentActionType?.slice(1)}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

MainLinkedAccounts.propTypes = {
    showMessage: PropTypes.func,
};

export default MainLinkedAccounts;